import { ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { FbEmrDemographicsWidget } from '../../../models/form-widget-emr-demographics.model';
import { WidgetBase } from '../../../models/widget-base';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { RxButtonComponent } from '@nrwl/rx-button';
import { RxIconComponent } from 'libs/rx-ui/src/lib/rx-icon/rx-icon.component';
import { WidgetApiService } from '../../../rx-form-presenter/services/widget-api.service';
import { DiscrepancyDetectionService } from '../../../rx-form-presenter/services/discrepancy-detection.service';
import { DateValidationService } from '../../../services/date-validation.service';
import { RxStatesComponent } from '../../../helpers/rx-states/rx-states.component';
import { parseLocalDateString } from '../../../helpers/date-utils';
import { MatDividerModule } from '@angular/material/divider';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';

@Component({
  selector: 'app-fb-emr-demographics',
  templateUrl: './fb-emr-demographics.component.html',
  styleUrls: ['./fb-emr-demographics.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    TokenToDivPipe,
    RxButtonComponent,
    RxIconComponent,
    RxStatesComponent,
    MatDividerModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
})
export class FbEmrDemographicsComponent extends WidgetBase implements OnInit {
  private _loadPending = false;
  private conflictingFields: Set<string> = new Set();
  private conflictDetails: Map<string, { saved: any; current: any }> = new Map();
  private _savedFormData: any;
  private _lastLoadedPatientId: string | null = null;
  private _isLoadingDemographics = false;

  @Input()
  public override set payload(value) {
    this._payload = value;
    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  @Input()
  public override set patientId(value) {
    this._patientId = value;
    const patientRemoteId = this._patient?.remote_id;
    if (patientRemoteId && !this.isPreview && !this.isPrinting && this.widgetApiService) {
      if (this._lastLoadedPatientId !== patientRemoteId && !this._isLoadingDemographics) {
        this._lastLoadedPatientId = patientRemoteId;
        this.loadDemographics(patientRemoteId.toString());
      }
    }
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set patient(value) {
    this._patient = value;
    if (value?.remote_id && !this.isPreview && !this.isPrinting && this.widgetApiService) {
      const patientId = value.remote_id.toString();
      if (this._lastLoadedPatientId !== patientId && !this._isLoadingDemographics) {
        this._lastLoadedPatientId = patientId;
        this.loadDemographics(patientId);
      }
    }
  }
  public override get patient() {
    return this._patient;
  }

  @Input()
  public override set isPreview(value: boolean) {
    super.isPreview = value;
    if (!value && !this.isPrinting && this._loadPending) {
      this.checkAndLoadDemographics();
    }
  }

  public override get isPreview(): boolean {
    return super.isPreview;
  }

  @Input()
  public override set isPrinting(value: boolean) {
    super.isPrinting = value;
    if (!value && !this.isPreview && this._loadPending) {
      this.checkAndLoadDemographics();
    }
  }

  public override get isPrinting(): boolean {
    return super.isPrinting;
  }

  @Input()
  public set savedFormData(value: any) {
    this._savedFormData = value;
  }

  public get savedFormData(): any {
    return this._savedFormData;
  }

  constructor(
    private injector: Injector,
    private widgetApiService: WidgetApiService,
    private cd: ChangeDetectorRef,
    private discrepancyDetectionService: DiscrepancyDetectionService,
    private dateValidationService: DateValidationService,
  ) {
    super(injector);
  }

  ngOnInit() {
    const patientRemoteId = this._patient?.remote_id;
    const patientIdToUse = patientRemoteId;

    if (patientIdToUse) {
      this._loadPending = true;
      if (!this.isPreview && !this.isPrinting && this.widgetApiService) {
        this._loadPending = false;
        this.loadDemographics(patientIdToUse);
      }
    }
  }

  private checkAndLoadDemographics() {
    const patientIdToUse = this._patient?.remote_id;
    if (patientIdToUse && !this.isPreview && !this.isPrinting && this.widgetApiService) {
      this._loadPending = false;
      this.loadDemographics(patientIdToUse);
    } else if (patientIdToUse) {
      this._loadPending = true;
    }
  }

  private loadDemographics(patientId: string) {
    if (this.isInstanceDesign || !this.widgetApiService || this._isLoadingDemographics) {
      return;
    }

    this._isLoadingDemographics = true;

    this.widgetApiService.getCustomerById(patientId).subscribe({
      next: (customer) => {
        this._isLoadingDemographics = false;
        if (customer) {
          this.prefillDemographics(customer);
        }
      },
      error: (error) => {
        this._isLoadingDemographics = false;
        console.error('Error loading customer demographics:', error);
      },
    });
  }

  private prefillDemographics(customer: any) {
    if (!this.payload.properties.items?.value) {
      return;
    }

    const formStatus = this.entity?.status;
    const isFormIncomplete = formStatus !== undefined && formStatus < 5;
    const hasSavedDemographicsMetadata = this.entity?.metadata?.demographics;

    // If form is incomplete and has saved metadata, merge PNE with metadata
    if (isFormIncomplete && hasSavedDemographicsMetadata) {
      // First layer: Prefill from PNE data
      this.prefillFromPNE(customer);

      // Second layer: Overlay with metadata (non-empty values override PNE)
      this.prefillFromDemographicsMetadataWithDiscrepancies(
        this.entity.metadata.demographics,
        customer,
      );
      return;
    }

    const savedFormData = this._savedFormData;
    if (savedFormData) {
      const discrepancies = this.discrepancyDetectionService.detectDemographicsDiscrepancies(
        savedFormData,
        customer,
      );

      this.conflictingFields.clear();
      this.conflictDetails.clear();
      discrepancies.forEach((d) => {
        this.conflictingFields.add(d.field);
        this.conflictDetails.set(d.field, {
          saved: d.saved,
          current: d.current,
        });
      });
    }

    const items = this.payload.properties.items.value;

    const personalInfo = items.find((item) => item.title === 'Personal Information' && item.nested);
    if (personalInfo?.items) {
      const firstName = personalInfo.items.find((field) => field.title === 'First Name');
      if (firstName) {
        if (!this.conflictingFields.has('First Name')) {
          firstName.value = customer.FirstName || '';
        }
        firstName.hasConflict = this.conflictingFields.has('First Name');
        if (firstName.hasConflict) {
          firstName.conflictTooltip = `PatientNow: ${
            this.conflictDetails.get('First Name')?.current || 'N/A'
          }`;
        }
      }

      const lastName = personalInfo.items.find((field) => field.title === 'Last Name');
      if (lastName) {
        if (!this.conflictingFields.has('Last Name')) {
          lastName.value = customer.LastName || '';
        }
        lastName.hasConflict = this.conflictingFields.has('Last Name');
        if (lastName.hasConflict) {
          lastName.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Last Name')?.current || 'N/A'}`;
        }
      }

      const middleName = personalInfo.items.find((field) => field.title === 'Middle Name');
      if (middleName) {
        if (!this.conflictingFields.has('Middle Name')) {
          middleName.value = customer.MiddleName || '';
        }
        middleName.hasConflict = this.conflictingFields.has('Middle Name');
        if (middleName.hasConflict) {
          middleName.conflictTooltip = `PatientNow: ${
            this.conflictDetails.get('Middle Name')?.current || 'N/A'
          }`;
        }
      }

      const genderItem = personalInfo.items.find((field) => field.title === 'Gender');
      if (genderItem) {
        if (!this.conflictingFields.has('Gender')) {
          genderItem.value = customer.Gender || '';
        }
        genderItem.hasConflict = this.conflictingFields.has('Gender');
        if (genderItem.hasConflict) {
          genderItem.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Gender')?.current || 'N/A'}`;
        }
      }

      const birthdayItem = personalInfo.items.find((field) => field.title === 'Birthday');
      if (birthdayItem) {
        if (!this.conflictingFields.has('Birthday')) {
          let birthdayValue = '';
          if (
            customer.Birthday &&
            !this.dateValidationService.shouldExcludeDate(customer.Birthday)
          ) {
            birthdayValue = parseLocalDateString(customer.Birthday);
          }
          birthdayItem.value = birthdayValue;
        }
        birthdayItem.hasConflict = this.conflictingFields.has('Birthday');
        if (birthdayItem.hasConflict) {
          birthdayItem.conflictTooltip = `PatientNow: ${
            this.conflictDetails.get('Birthday')?.current || 'N/A'
          }`;
        }
      }
    }

    const standaloneGenderItem = items.find((item) => item.title === 'Gender' && !item.nested);
    if (standaloneGenderItem) {
      if (!this.conflictingFields.has('Gender')) {
        standaloneGenderItem.value = customer.Gender || '';
      }
      standaloneGenderItem.hasConflict = this.conflictingFields.has('Gender');
    }

    const standaloneBirthdayItem = items.find((item) => item.title === 'Birthday' && !item.nested);
    if (standaloneBirthdayItem) {
      if (!this.conflictingFields.has('Birthday')) {
        let birthdayValue = '';
        if (customer.Birthday && !this.dateValidationService.shouldExcludeDate(customer.Birthday)) {
          birthdayValue = parseLocalDateString(customer.Birthday);
        }
        standaloneBirthdayItem.value = birthdayValue;
      }
      standaloneBirthdayItem.hasConflict = this.conflictingFields.has('Birthday');
    }

    const addressInfo = items.find((item) => item.title === 'Address' && item.nested);
    if (addressInfo?.items) {
      const address1 = addressInfo.items.find((field) => field.title === 'Address 1');
      if (address1) {
        if (!this.conflictingFields.has('Address 1')) {
          address1.value = customer.Address || '';
        }
        address1.hasConflict = this.conflictingFields.has('Address 1');
        if (address1.hasConflict) {
          address1.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Address 1')?.current || 'N/A'}`;
        }
      }

      const address2 = addressInfo.items.find((field) => field.title === 'Address 2');
      if (address2) {
        if (!this.conflictingFields.has('Address 2')) {
          address2.value = customer.Address2 || '';
        }
        address2.hasConflict = this.conflictingFields.has('Address 2');
        if (address2.hasConflict) {
          address2.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Address 2')?.current || 'N/A'}`;
        }
      }

      const city = addressInfo.items.find((field) => field.title === 'City');
      if (city) {
        if (!this.conflictingFields.has('City')) {
          city.value = customer.City || '';
        }
        city.hasConflict = this.conflictingFields.has('City');
        if (city.hasConflict) {
          city.conflictTooltip = `PatientNow: ${this.conflictDetails.get('City')?.current || 'N/A'}`;
        }
      }

      const state = addressInfo.items.find((field) => field.title === 'State');
      if (state) {
        if (!this.conflictingFields.has('State')) {
          state.value = customer.State?.toUpperCase() || '';
        }
        state.hasConflict = this.conflictingFields.has('State');
        if (state.hasConflict) {
          state.conflictTooltip = `PatientNow: ${this.conflictDetails.get('State')?.current || 'N/A'}`;
        }
      }

      const zip = addressInfo.items.find((field) => field.title === 'Zip/Postal');
      if (zip) {
        if (!this.conflictingFields.has('Zip/Postal')) {
          zip.value = customer.Zipcode || '';
        }
        zip.hasConflict = this.conflictingFields.has('Zip/Postal');
        if (zip.hasConflict) {
          zip.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Zip/Postal')?.current || 'N/A'}`;
        }
      }
    }

    // Prefill Contact Information
    const contactInfo = items.find((item) => item.title === 'Contact Information' && item.nested);
    if (contactInfo?.items) {
      const mobilePhone = contactInfo.items.find((field) => field.title === 'Mobile Phone');
      if (mobilePhone) {
        if (!this.conflictingFields.has('Mobile Phone')) {
          mobilePhone.value = customer.MobilePhone || '';
        }
        mobilePhone.hasConflict = this.conflictingFields.has('Mobile Phone');
        if (mobilePhone.hasConflict) {
          mobilePhone.conflictTooltip = `PatientNow: ${
            this.conflictDetails.get('Mobile Phone')?.current || 'N/A'
          }`;
        }
      }

      const email = contactInfo.items.find((field) => field.title === 'Email');
      if (email) {
        if (!this.conflictingFields.has('Email')) {
          email.value = customer.Email || '';
        }
        email.hasConflict = this.conflictingFields.has('Email');
        if (email.hasConflict) {
          email.conflictTooltip = `PatientNow: ${this.conflictDetails.get('Email')?.current || 'N/A'}`;
        }
      }
    }

    this.cd.detectChanges();
  }

  private prefillFromPNE(customer: any) {
    if (!this.payload.properties.items?.value) {
      return;
    }

    const items = this.payload.properties.items.value;

    // Prefill Personal Information
    const personalInfo = items.find((item) => item.title === 'Personal Information' && item.nested);
    if (personalInfo?.items) {
      const firstName = personalInfo.items.find((field) => field.title === 'First Name');
      if (firstName) {
        firstName.value = customer.FirstName || '';
      }

      const lastName = personalInfo.items.find((field) => field.title === 'Last Name');
      if (lastName) {
        lastName.value = customer.LastName || '';
      }

      const middleName = personalInfo.items.find((field) => field.title === 'Middle Name');
      if (middleName) {
        middleName.value = customer.MiddleName || '';
      }

      const genderItem = personalInfo.items.find((field) => field.title === 'Gender');
      if (genderItem) {
        genderItem.value = customer.Gender || '';
      }

      const birthdayItem = personalInfo.items.find((field) => field.title === 'Birthday');
      if (birthdayItem && customer.Birthday) {
        birthdayItem.value = parseLocalDateString(customer.Birthday);
      }
    }

    // Prefill Address Information
    const addressInfo = items.find((item) => item.title === 'Address' && item.nested);
    if (addressInfo?.items) {
      const address1 = addressInfo.items.find((field) => field.title === 'Address 1');
      if (address1) {
        address1.value = customer.Address || '';
      }

      const address2 = addressInfo.items.find((field) => field.title === 'Address 2');
      if (address2) {
        address2.value = customer.Address2 || '';
      }

      const city = addressInfo.items.find((field) => field.title === 'City');
      if (city) {
        city.value = customer.City || '';
      }

      const state = addressInfo.items.find((field) => field.title === 'State');
      if (state) {
        state.value = customer.State?.toUpperCase() || '';
      }

      const zip = addressInfo.items.find((field) => field.title === 'Zip/Postal');
      if (zip) {
        zip.value = customer.Zipcode || '';
      }
    }

    // Prefill Contact Information
    const contactInfo = items.find((item) => item.title === 'Contact Information' && item.nested);
    if (contactInfo?.items) {
      const mobilePhone = contactInfo.items.find((field) => field.title === 'Mobile Phone');
      if (mobilePhone) {
        mobilePhone.value = customer.MobilePhone || '';
      }

      const email = contactInfo.items.find((field) => field.title === 'Email');
      if (email) {
        email.value = customer.Email || '';
      }
    }
  }

  private prefillFromDemographicsMetadataWithDiscrepancies(
    demographicsMetadata: any,
    customer: any,
  ) {
    if (!this.payload.properties.items?.value || !demographicsMetadata) {
      return;
    }

    const items = this.payload.properties.items.value;

    // Field mapping from metadata field names to form titles
    const reverseFieldMapping: { [key: string]: string } = {
      first_name: 'First Name',
      last_name: 'Last Name',
      middle_name: 'Middle Name',
      gender: 'Gender',
      birthday: 'Birthday',
      address1: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      state: 'State',
      postal: 'Zip/Postal',
      work_phone: 'Mobile Phone',
      email: 'Email',
    };

    this.conflictingFields.clear();
    this.conflictDetails.clear();

    // Process nested items (Personal Information, Address, Contact Information)
    for (const item of items) {
      if (item.nested && item.items && Array.isArray(item.items)) {
        if (item.visible !== false) {
          item.value = true;

          for (const field of item.items) {
            if (field.visible !== false) {
              const metadataKey = Object.keys(reverseFieldMapping).find(
                (key) => reverseFieldMapping[key] === field.title,
              );

              if (metadataKey && demographicsMetadata[metadataKey] !== undefined) {
                let metadataValue = demographicsMetadata[metadataKey];

                if (metadataKey === 'middle_name' && metadataValue === '') {
                  continue;
                }

                // Special handling for state
                if (metadataKey === 'state' && metadataValue) {
                  metadataValue = metadataValue.toUpperCase();
                }

                // Check for discrepancy with current field value (from PNE)
                const currentValue = field.value;
                if (metadataValue !== '' && currentValue !== '' && metadataValue !== currentValue) {
                  this.conflictingFields.add(field.title);
                  this.conflictDetails.set(field.title, {
                    saved: metadataValue,
                    current: currentValue,
                  });

                  // Mark field with conflict
                  field.hasConflict = true;
                  field.conflictTooltip = `PatientNow: ${currentValue || 'N/A'}`;
                }

                // Set the metadata value (metadata takes precedence for user edits)
                if (metadataValue !== '') {
                  field.value = metadataValue;
                }
              }
            }
          }
        }
      }
    }

    this.cd.detectChanges();
  }

  private prefillFromDemographicsMetadata(demographicsMetadata: any) {
    if (!this.payload.properties.items?.value || !demographicsMetadata) {
      return;
    }

    const items = this.payload.properties.items.value;

    // Field mapping from metadata field names to form titles
    const reverseFieldMapping: { [key: string]: string } = {
      first_name: 'First Name',
      last_name: 'Last Name',
      middle_name: 'Middle Name',
      gender: 'Gender',
      birthday: 'Birthday',
      address1: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      state: 'State',
      postal: 'Zip/Postal',
      work_phone: 'Mobile Phone',
      email: 'Email',
    };

    // Process all items and populate from metadata
    for (const item of items) {
      if (item.nested && item.items && Array.isArray(item.items)) {
        // Process nested items (Personal Information, Address, Contact Information)
        if (item.visible !== false) {
          item.value = true; // Make sure the section is enabled

          for (const field of item.items) {
            if (field.visible !== false) {
              // Find corresponding metadata value
              const metadataKey = Object.keys(reverseFieldMapping).find(
                (key) => reverseFieldMapping[key] === field.title,
              );

              if (metadataKey && demographicsMetadata[metadataKey] !== undefined) {
                let value = demographicsMetadata[metadataKey];
                if (metadataKey === 'state') {
                  value = demographicsMetadata[metadataKey]?.toUpperCase();
                }

                field.value = value;
              }
            }
          }
        }
      } else if (!item.nested && item.visible !== false) {
        // Process single fields (for backward compatibility or non-nested fields)
        const metadataKey = Object.keys(reverseFieldMapping).find(
          (key) => reverseFieldMapping[key] === item.title,
        );

        if (metadataKey && demographicsMetadata[metadataKey] !== undefined) {
          let value = demographicsMetadata[metadataKey];

          item.value = value;
        }
      }
    }

    this.cd.detectChanges();
  }

  override isValid() {
    return new FbEmrDemographicsWidget(this.payload).isValid();
  }

  getValidationErrorMessage() {
    const widget = new FbEmrDemographicsWidget(this.payload);
    const errors = widget.getValidationErrors();
    if (errors.length === 0) {
      return 'This field is required';
    }
    return 'Required fields: ' + errors.join(', ');
  }

  // Commented out to let rx-form-presenter handle the metadata wrapping
  // This ensures demographics metadata is wrapped like medical history metadata
  // public getMetadata() {
  //     return new FbEmrDemographicsWidget(this.payload).getMetadata();
  // }

  hasVisibleCategories(): boolean {
    if (!this.payload.properties.items?.value) {
      return false;
    }

    return this.payload.properties.items.value.some((category) => {
      if (category.nested) {
        return category.value === true && category.visible !== false;
      }
      return category.visible !== false;
    });
  }

  isSubmissionPending(): boolean {
    const form = this.entity as any;
    return form?.status === 2 && form?.shared_with_patient === true;
  }

  getCategoryTitle(category: any): string {
    return category.title || '';
  }

  getFields(category: any): any[] {
    return category?.items || [];
  }

  /**
   * True while a patient's own plan is being customized. The widget then draws empty fields and
   * never reads or writes the saved values, so they are stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  private readonly defaultStateFields = new WeakMap<object, any>();

  public displayValue(target: any): any {
    return this.isInstanceDesign ? '' : target.value;
  }

  public onValueChange(target: any, value: any): void {
    if (this.isInstanceDesign) {
      return;
    }

    target.value = value;
    this.clearScribeSuggestionMarker(target);
  }

  public clearMarker(target: any): void {
    if (this.isInstanceDesign) {
      return;
    }

    this.clearScribeSuggestionMarker(target);
  }

  public getDisplayField(field: any): any {
    if (!this.isInstanceDesign) {
      return field;
    }

    let copy = this.defaultStateFields.get(field);
    if (!copy) {
      copy = { ...field, value: '' };
      this.defaultStateFields.set(field, copy);
    }
    return copy;
  }

  public isConflicting(target: any): boolean {
    return !this.isInstanceDesign && !!target?.hasConflict;
  }

  hasItems(category: any): boolean {
    return category?.items && category?.items?.length > 0;
  }

  isNestedCategory(item: any): boolean {
    return item?.nested === true;
  }

  isFirstVisibleNestedCategory(index: number): boolean {
    const items = this.payload?.properties?.items?.value;
    if (!Array.isArray(items)) {
      return false;
    }

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (this.isNestedCategory(item) && item?.value && item?.visible !== false) {
        return i === index;
      }
    }

    return false;
  }

  getColumnClass(index: number): string {
    return index % 2 === 0 ? 'field-left' : 'field-right';
  }

  toggleTooltip(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.toggle('show-tooltip');

    setTimeout(() => {
      target.classList.remove('show-tooltip');
    }, 3000);
  }
}
